/**
 * Date formatting utilities for consistent date handling across the application
 */

export interface DateFormatOptions {
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  year?: 'numeric' | '2-digit';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  second?: 'numeric' | '2-digit';
}

/**
 * Format a date string to a localized date format with error handling
 * @param dateInput - Date string, Date object, or timestamp
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @param locale - Locale string (defaults to 'en-US')
 * @returns Formatted date string or fallback message
 */
export function formatDate(
  dateInput: string | Date | number | null | undefined,
  options: DateFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  },
  locale: string = 'en-US'
): string {
  // Handle null/undefined inputs
  if (dateInput === null || dateInput === undefined) {
    return 'No Date';
  }

  // Handle empty strings
  if (typeof dateInput === 'string' && dateInput.trim() === '') {
    return 'No Date';
  }

  try {
    let date: Date;

    // Parse different input types
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'number') {
      date = new Date(dateInput);
    } else if (typeof dateInput === 'string') {
      // Handle various string formats
      date = new Date(dateInput);
    } else {
      // Try to convert unknown types
      date = new Date(String(dateInput));
    }

    // Validate the parsed date
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    // Format the date
    const formatted = date.toLocaleDateString(locale, options);
    return formatted;
  } catch (error) {
    return 'Invalid Date';
  }
}

/**
 * Format date for Baltic Exchange display (shorthand)
 */
export function formatBalticDate(dateInput: string | Date | number | null | undefined): string {
  return formatDate(dateInput, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date with time for detailed display
 */
export function formatDateTime(dateInput: string | Date | number | null | undefined): string {
  return formatDate(dateInput, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Get relative time string (e.g., "2 days ago")
 */
export function getRelativeTime(dateInput: string | Date | number | null | undefined): string {
  if (!dateInput) return 'Unknown time';

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'Invalid time';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    return 'Unknown time';
  }
}

/**
 * Check if a date is today
 */
export function isToday(dateInput: string | Date | number | null | undefined): boolean {
  if (!dateInput) return false;

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return false;

    const today = new Date();
    return date.toDateString() === today.toDateString();
  } catch (error) {
    return false;
  }
}

/**
 * Check if a date is valid
 */
export function isValidDate(dateInput: string | Date | number | null | undefined): boolean {
  if (!dateInput) return false;

  try {
    const date = new Date(dateInput);
    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
}

/**
 * Ensure a value is a valid Date object.
 * Handles strings (from JSON.parse), numbers (timestamps), and Date objects.
 * Returns fallback if the value cannot be converted to a valid Date.
 */
export function toSafeDate(
  value: Date | string | number | null | undefined,
  fallback: Date = new Date()
): Date {
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (value == null) return fallback;

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? fallback : parsed;
}

/**
 * Normalize date fields on a Conversation object so that
 * createdAt / updatedAt / lastMessageAt are always real Date instances.
 * This guards against date-type loss through JSON serialization
 * (e.g. localStorage fallback in the persistence middleware).
 */
export function normalizeConversationDates<
  T extends { createdAt?: unknown; updatedAt?: unknown; lastMessageAt?: unknown },
>(conversation: T): T {
  const now = new Date();
  return {
    ...conversation,
    createdAt: toSafeDate(conversation.createdAt as Date | string | number | null, now),
    updatedAt: toSafeDate(conversation.updatedAt as Date | string | number | null, now),
    ...(conversation.lastMessageAt !== undefined && {
      lastMessageAt: toSafeDate(conversation.lastMessageAt as Date | string | number | null, now),
    }),
  };
}

/**
 * Split a datetime into the `value` strings that `<input type="date">` and
 * `<input type="time">` expect, both read in the viewer's local timezone.
 *
 * Both halves have to come off the same clock. Taking the date from
 * `toISOString()` (UTC) while taking the time from `toTimeString()` (local)
 * puts them a day apart whenever the local time sits on the far side of
 * midnight UTC - so an 01:00 arrival in UTC+2 opens showing the previous day.
 */
export function toDateTimeInputValues(dateInput: string | Date): { date: string; time: string } {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) {
    return { date: '', time: '' };
  }

  const pad = (value: number) => String(value).padStart(2, '0');

  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
}
